// v0.6.9 Phase 2 — Settings > VAT Rates manual override, folded into
// the existing "rate_tables" tab (nested panel, same convention as
// RateVerifyPanel/RatePendingAcksPanel right below it) rather than a
// new top-level tab, since every VAT-rate concern belongs in one
// place. TEDB stays exactly as it was — read-only fetch-and-flag; this
// panel is a SEPARATE, operator-entered source that only affects a
// calc once the precedence toggle below is explicitly flipped.
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DownloadSimple, Scales, Trash, UploadSimple } from "@phosphor-icons/react";
import {
  listVatRateOverrides, uploadVatRateOverride, deleteVatRateOverride,
  patchSettings,
} from "../lib/authApi";
import {
  downloadVatRatesTemplate, downloadResolvedVatRates,
} from "../lib/api/downloads";

export const VatRateOverridesPanel = ({ isAdmin, settings, onSaved }) => {
  const { t } = useTranslation();
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [precedenceBusy, setPrecedenceBusy] = useState(false);
  const [precedenceConfirm, setPrecedenceConfirm] = useState(null);  // v0.6.10: confirmation state

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listVatRateOverrides();
      setOverrides(data?.overrides || []);
    } catch {
      /* leave list as-is; the panel still renders */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) reload();
  }, [isAdmin, reload]);

  if (!isAdmin) return null;

  const precedence = settings?.vat_rate_precedence || "repo_table";

  // v0.6.10: show confirmation dialog before flipping precedence.
  // This prevents accidents where one click changes rates for all future calcs.
  const onPrecedenceChange = async (e) => {
    const next = e.target.value;
    if (next === precedence) return;  // No change, close dialog
    setPrecedenceConfirm(next);  // Show dialog, don't commit yet
  };

  const confirmPrecedenceChange = async (newValue) => {
    setPrecedenceConfirm(null);
    setPrecedenceBusy(true);
    try {
      const saved = await patchSettings({ vat_rate_precedence: newValue });
      onSaved(saved);
    } finally {
      setPrecedenceBusy(false);
    }
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setUploadResult(null);
    try {
      const result = await uploadVatRateOverride(file);
      setUploadResult(result);
      await reload();
    } catch (err) {
      setUploadResult({
        uploaded: 0, failed: 0,
        failures: [err?.response?.data?.detail || err?.message || "Upload failed"],
      });
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id) => {
    setBusy(true);
    try {
      await deleteVatRateOverride(id);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="vat-rate-overrides-panel"
      className="mt-4 border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 rounded-xl p-4 space-y-3"
    >
      <div className="text-[10px] tracking-normal text-teal-700 dark:text-teal-300 flex items-center gap-1">
        <Scales size={12} weight="bold" /> {t("ui.settings.vat_overrides.title", { defaultValue: "Manual rate override" })}
      </div>
      <p className="text-[11px] text-slate-500 dark:text-slate-400 max-w-2xl">
        {t("ui.settings.vat_overrides.intro", { defaultValue: "Enter a dated rate that overrides the repo-versioned tables above for a specific country and category — for a jurisdiction change the tables haven't caught up with yet. TEDB is unaffected; it stays a read-only check against the repo tables either way. It applies to every return for those dates — the national returns and the OSS return alike." })}
      </p>

      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[11px] text-slate-600 dark:text-slate-300">{t("ui.settings.vat_overrides.precedence_label", { defaultValue: "Precedence:" })}</span>
        <select
          data-testid="vat-rate-precedence-select"
          value={precedence}
          disabled={precedenceBusy}
          onChange={onPrecedenceChange}
          className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-50 px-2 py-1 text-xs rounded-xl disabled:opacity-50"
        >
          <option value="repo_table">{t("ui.settings.vat_overrides.opt_repo", { defaultValue: "Repo tables (default)" })}</option>
          <option value="manual_override">{t("ui.settings.vat_overrides.opt_manual", { defaultValue: "Manual override" })}</option>
        </select>
        <span className="text-[10px] text-slate-500 dark:text-slate-400">
          {precedence === "manual_override"
            ? t("ui.settings.vat_overrides.manual_hint", { defaultValue: "A manual override wins when both cover the same date; the repo table is still used where no override exists." })
            : t("ui.settings.vat_overrides.repo_hint", { defaultValue: "Repo tables win when both cover the same date; a manual override only applies where the repo table has no coverage." })}
        </span>
      </div>

      <div className="flex items-center gap-4 text-xs">
        <span data-testid="vat-rate-overrides-summary" className="text-slate-600 dark:text-slate-300">
          {loading ? t("ui.settings.vat_overrides.loading", { defaultValue: "Loading…" }) : (
            overrides.length === 0
              ? t("ui.settings.vat_overrides.empty", { defaultValue: "No manual overrides saved yet." })
              : t("ui.settings.vat_overrides.summary", { n: overrides.length, defaultValue: `${overrides.length} override(s) saved.` })
          )}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="vat-rates-download-template"
          onClick={downloadVatRatesTemplate}
          className="inline-flex items-center gap-1 text-xs font-bold text-teal-700 dark:text-teal-300 bg-teal-600/10 hover:bg-teal-600/20 border border-teal-400/50 px-3 py-1.5 rounded-xl"
        >
          <DownloadSimple size={13} weight="bold" /> {t("ui.settings.vat_overrides.download_template", { defaultValue: "Download template" })}
        </button>
        <button
          type="button"
          data-testid="vat-rates-download-resolved"
          onClick={downloadResolvedVatRates}
          className="inline-flex items-center gap-1 text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-600/10 hover:bg-slate-600/20 border border-slate-400/50 px-3 py-1.5 rounded-xl"
        >
          <DownloadSimple size={13} weight="bold" /> {t("ui.settings.vat_overrides.download_resolved", { defaultValue: "Download what a calc today would use" })}
        </button>
        <label
          data-testid="vat-rates-upload-label"
          className={`inline-flex items-center gap-1 text-xs font-bold text-white bg-teal-600 hover:bg-teal-700 border border-teal-700 px-3 py-1.5 rounded-xl cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}
        >
          <UploadSimple size={13} weight="bold" /> {t("ui.settings.vat_overrides.upload_filled", { defaultValue: "Upload filled template" })}
          <input
            type="file"
            data-testid="vat-rates-upload-input"
            accept=".xlsx"
            onChange={onFileChosen}
            className="hidden"
            disabled={busy}
          />
        </label>
      </div>

      {uploadResult && (
        <div data-testid="vat-rates-upload-result" className="text-xs border border-slate-200 dark:border-slate-700 rounded-xl p-3 space-y-1 bg-white dark:bg-slate-900">
          <div className="text-emerald-700 dark:text-emerald-300">
            {t("ui.settings.fx_presets.rows_saved", { n: uploadResult.uploaded, defaultValue: `${uploadResult.uploaded} row(s) saved.` })}
          </div>
          {uploadResult.failed > 0 && (
            <div className="text-amber-700 dark:text-amber-300">
              {t("ui.settings.fx_presets.rows_skipped", { n: uploadResult.failed, defaultValue: `${uploadResult.failed} row(s) skipped:` })}
              <ul className="list-disc list-inside mt-1">
                {(uploadResult.failures || []).map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {overrides.length > 0 && (
        <div className="border border-slate-200 dark:border-slate-700 max-h-64 overflow-y-auto bg-white dark:bg-slate-900">
          {overrides.map((o) => (
            <div
              key={o.id}
              data-testid={`vat-rate-override-row-${o.id}`}
              className="grid grid-cols-12 gap-2 px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-xs items-center"
            >
              <span className="col-span-2 tabular-nums text-slate-900 dark:text-slate-50">{o.country}</span>
              <span className="col-span-2 tabular-nums text-slate-600 dark:text-slate-300">{o.category}</span>
              <span className="col-span-1 tabular-nums text-teal-700 dark:text-teal-300">{o.rate}</span>
              <span className="col-span-2 tabular-nums text-slate-500 dark:text-slate-400">{o.effective_from} → {o.effective_to}</span>
              {/* v0.6.10 — under default "repo_table" precedence, an
                  override only actually applies where the repo table
                  has NO cell for that country+category — otherwise
                  it's a complete no-op. Every row used to render
                  identically regardless, so an operator uploading a
                  "correction" to a value the repo table already
                  defines saw it listed as saved with no indication it
                  was inert. */}
              <span
                data-testid={`vat-rate-override-status-${o.id}`}
                className={`col-span-2 font-bold ${
                  o.status === "active"
                    ? "text-emerald-700 dark:text-emerald-300"
                    : o.status === "shadowed_by_repo_table"
                      ? "text-amber-700 dark:text-amber-300"
                      : "text-slate-500 dark:text-slate-400"
                }`}
                title={
                  o.status === "shadowed_by_repo_table"
                    ? "The repo table already defines this country+category — this override is saved but currently has no effect. Switch precedence to \"Manual override\" above to make it apply."
                    : undefined
                }
              >
                {o.status === "active" ? t("ui.settings.vat_overrides.status_active", { defaultValue: "Active" })
                  : o.status === "shadowed_by_repo_table" ? t("ui.settings.vat_overrides.status_shadowed", { defaultValue: "Shadowed by repo table" })
                    : o.status === "scheduled" ? t("ui.settings.vat_overrides.status_scheduled", { defaultValue: "Not yet in effect" })
                      : o.status === "expired" ? t("ui.settings.vat_overrides.status_expired", { defaultValue: "Expired" })
                        : ""}
              </span>
              <span className="col-span-2 text-slate-500 dark:text-slate-400 truncate">{o.notes}</span>
              <span className="col-span-1 text-right">
                <button
                  type="button"
                  data-testid={`vat-rate-override-delete-${o.id}`}
                  onClick={() => onDelete(o.id)}
                  disabled={busy}
                  title={t("ui.settings.remove_entry", { defaultValue: "Remove this entry" })}
                  className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 disabled:opacity-50"
                >
                  <Trash size={13} weight="bold" />
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* v0.6.10 — precedence toggle confirmation dialog */}
      {precedenceConfirm && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-lg max-w-md">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-sm font-bold text-slate-900 dark:text-slate-50">
                {t("ui.settings.vat_overrides.confirm_title", { defaultValue: "Change VAT Rate Precedence?" })}
              </h2>
            </div>
            <div className="px-6 py-4 space-y-3">
              <p className="text-xs text-slate-600 dark:text-slate-300">
                {t("ui.settings.vat_overrides.confirm_body_before", { defaultValue: "This will change which rates are used for" })}{" "}
                <strong>{t("ui.settings.vat_overrides.confirm_body_strong", { defaultValue: "all future calculations" })}</strong>
                {t("ui.settings.vat_overrides.confirm_body_after", { defaultValue: "." })}
              </p>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <p className="text-xs text-amber-900 dark:text-amber-300">
                  <strong>{t("ui.settings.vat_overrides.current", { defaultValue: "Current:" })}</strong>{" "}
                  {precedence === "repo_table"
                    ? t("ui.settings.vat_overrides.repo_wins", { defaultValue: "Repo tables win" })
                    : t("ui.settings.vat_overrides.manual_wins", { defaultValue: "Manual override wins" })}
                  <br />
                  <strong>{t("ui.settings.vat_overrides.after", { defaultValue: "After:" })}</strong>{" "}
                  {precedenceConfirm === "repo_table"
                    ? t("ui.settings.vat_overrides.repo_wins", { defaultValue: "Repo tables win" })
                    : t("ui.settings.vat_overrides.manual_wins", { defaultValue: "Manual override wins" })}
                </p>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t("ui.settings.vat_overrides.confirm_warning", { defaultValue: "This cannot be easily undone. Are you sure?" })}
              </p>
            </div>
            <div className="px-6 py-3 border-t border-slate-200 dark:border-slate-700 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setPrecedenceConfirm(null)}
                disabled={precedenceBusy}
                className="text-xs font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-50 px-3 py-1.5 rounded-lg disabled:opacity-50"
              >
                {t("ui.freeze.confirm.cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="button"
                onClick={() => confirmPrecedenceChange(precedenceConfirm)}
                disabled={precedenceBusy}
                className="text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 px-3 py-1.5 rounded-lg disabled:opacity-50"
              >
                {precedenceBusy ? t("ui.header.my_name.saving", { defaultValue: "Saving…" }) : t("ui.settings.vat_overrides.confirm_yes", { defaultValue: "Yes, flip precedence" })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
